import axios from 'axios';

const THEME_API_URL = 'https://frontend-prod.blinkz.in/theme';
const CLIENT_ID = 'D10003';

/**
 * Default theme configuration to use as fallback
 */
export const defaultThemeConfig = {
  preset: 'instant',
  fonts: {
    body: 'Inter',
    heading: 'Lato',
  },
  navbar: {
    backgroundColor: '#F1F2F4',
    linkColor: '#080808',
    linkHoverColor: '#d210f9',
    mobileMenuBg: '#F1F2F4',
    mobileMenuTextColor: '#080808',
    mobileMenuHoverColor: '#d210f9',
    navbarMobileSyncWithDesktop: true,
  },
  buttons: {
    primary: {
      bg: '#ffffff',
      text: '#050505',
      hoverBg: '#0a0a0a',
      hoverText: '#ffffff',
    },
    secondary: {
      bg: '#050505',
      text: '#fdfcfc',
      hoverBg: '#fafafa',
      hoverText: '#333333',
    },
    tertiary: {
      bg: 'transparent',
      text: '#050505',
      hoverBg: '#F1F2F4',
      hoverText: '#050505',
    },
  },
  landingSection: {
    text: 'Expert Hub',
    image: null,
    color1: '#F4EEE7',
    color2: '#ffffff',
  },
  banner: {
    textColor: '#FFFFFF',
    backgroundColor: '#151414',
  },
  faq: {
    // Off by default — only turns on once a seller actually fills in
    // questions via the theme settings FAQ plugin. Never auto-populated
    // with placeholder content.
    enabled: false,
    heading: 'Frequently Asked Questions',
    subheading: "Answers to the questions we hear most. Can't find what you're looking for? Reach out to our support team.",
    // '' = show as a direct navbar link, otherwise the pages are nested under a dropdown with this label
    navGroup: '',
    displayInNav: true,
    displayInFooter: true,
    footerGroup: 'Quick Links',
    items: [],
  },
  // Off by default — only shows the store address/support phone in the
  // footer once a seller opts in via the theme settings Footer toggle.
  showFooterAddress: false,
  // Payment method badges shown in the footer (e.g. ["visa","upi","cod"]).
  // Empty by default — a seller should only advertise methods they
  // actually support, not a default set that may not apply to them.
  paymentMethods: [],
};

/**
 * Fetch theme configuration from API
 * @param {string} clientId - The shop/client ID to fetch theme for
 * @returns {Promise<object>} Theme configuration object
 */
export const fetchThemeConfig = async (clientId = CLIENT_ID) => {
  try {
    const response = await axios.get(THEME_API_URL, {
      headers: {
        'Client-Id': clientId,
      },
    });

    if (response.data && response.data.settings) {
      return normalizeThemeConfig(response.data.settings);
    }

    return defaultThemeConfig;
  } catch (error) {
    console.warn('Failed to fetch theme configuration, using default config:', error.message);
    return defaultThemeConfig;
  }
};

/**
 * Normalize the API response to match the expected config structure
 * @param {object} apiSettings - Settings object from API response
 * @returns {object} Normalized theme configuration
 */
const normalizeThemeConfig = (apiSettings) => {
  return {
    preset: apiSettings.preset || defaultThemeConfig.preset,
    fonts: {
      body: apiSettings.fonts?.body || defaultThemeConfig.fonts.body,
      heading: apiSettings.fonts?.heading || defaultThemeConfig.fonts.heading,
    },
    navbar: {
      backgroundColor: apiSettings.navbar?.backgroundColor || defaultThemeConfig.navbar.backgroundColor,
      linkColor: apiSettings.navbar?.linkColor || defaultThemeConfig.navbar.linkColor,
      linkHoverColor: apiSettings.navbar?.linkHoverColor || defaultThemeConfig.navbar.linkHoverColor,
      mobileMenuBg: apiSettings.navbar?.mobileMenuBg || defaultThemeConfig.navbar.mobileMenuBg,
      mobileMenuTextColor: apiSettings.navbar?.mobileMenuTextColor || defaultThemeConfig.navbar.mobileMenuTextColor,
      mobileMenuHoverColor: apiSettings.navbar?.mobileMenuHoverColor || defaultThemeConfig.navbar.mobileMenuHoverColor,
      navbarMobileSyncWithDesktop: apiSettings.navbar?.navbarMobileSyncWithDesktop !== undefined
        ? apiSettings.navbar.navbarMobileSyncWithDesktop
        : defaultThemeConfig.navbar.navbarMobileSyncWithDesktop,
    },
    buttons: {
      primary: {
        bg: apiSettings.buttons?.primary?.bg || defaultThemeConfig.buttons.primary.bg,
        text: apiSettings.buttons?.primary?.text || defaultThemeConfig.buttons.primary.text,
        hoverBg: apiSettings.buttons?.primary?.hoverBg || defaultThemeConfig.buttons.primary.hoverBg,
        hoverText: apiSettings.buttons?.primary?.hoverText || defaultThemeConfig.buttons.primary.hoverText,
      },
      secondary: {
        bg: apiSettings.buttons?.secondary?.bg || defaultThemeConfig.buttons.secondary.bg,
        text: apiSettings.buttons?.secondary?.text || defaultThemeConfig.buttons.secondary.text,
        hoverBg: apiSettings.buttons?.secondary?.hoverBg || defaultThemeConfig.buttons.secondary.hoverBg,
        hoverText: apiSettings.buttons?.secondary?.hoverText || defaultThemeConfig.buttons.secondary.hoverText,
      },
      tertiary: {
        bg: apiSettings.buttons?.tertiary?.bg || defaultThemeConfig.buttons.tertiary.bg,
        text: apiSettings.buttons?.tertiary?.text || defaultThemeConfig.buttons.tertiary.text,
        hoverBg: apiSettings.buttons?.tertiary?.hoverBg || defaultThemeConfig.buttons.tertiary.hoverBg,
        hoverText: apiSettings.buttons?.tertiary?.hoverText || defaultThemeConfig.buttons.tertiary.hoverText,
      },
    },
    landingSection: {
      text: apiSettings.landingSection?.text || defaultThemeConfig.landingSection.text,
      image: apiSettings.landingSection?.image || defaultThemeConfig.landingSection.image,
      color1: apiSettings.landingSection?.color1 || defaultThemeConfig.landingSection.color1,
      color2: apiSettings.landingSection?.color2 || defaultThemeConfig.landingSection.color2,
    },
    banner: {
      textColor: apiSettings.banner?.textColor || defaultThemeConfig.banner.textColor,
      backgroundColor: apiSettings.banner?.backgroundColor || defaultThemeConfig.banner.backgroundColor,
    },
    faq: {
      enabled: apiSettings.faq?.enabled !== undefined ? apiSettings.faq.enabled : defaultThemeConfig.faq.enabled,
      heading: apiSettings.faq?.heading || defaultThemeConfig.faq.heading,
      subheading: apiSettings.faq?.subheading || defaultThemeConfig.faq.subheading,
      navGroup: apiSettings.faq?.navGroup !== undefined ? apiSettings.faq.navGroup : defaultThemeConfig.faq.navGroup,
      displayInNav: apiSettings.faq?.displayInNav !== undefined ? apiSettings.faq.displayInNav : defaultThemeConfig.faq.displayInNav,
      displayInFooter: apiSettings.faq?.displayInFooter !== undefined ? apiSettings.faq.displayInFooter : defaultThemeConfig.faq.displayInFooter,
      footerGroup: apiSettings.faq?.footerGroup || defaultThemeConfig.faq.footerGroup,
      items: apiSettings.faq?.items || defaultThemeConfig.faq.items,
    },
    showFooterAddress: apiSettings.showFooterAddress !== undefined
      ? apiSettings.showFooterAddress
      : defaultThemeConfig.showFooterAddress,
    paymentMethods: Array.isArray(apiSettings.paymentMethods)
      ? apiSettings.paymentMethods
      : defaultThemeConfig.paymentMethods,
  };
};
